'use client'

import { processPendingAddressesAction } from '@/lib/services/actions/address/process-pending-addresses'
import { useAppDispatch, useAppSelector } from '@/lib/store'
import { clearPendingAddresses } from '@/lib/store/shared/pendingData'
import { RootState } from '@/lib/store/store'
import { showToast } from '@/lib/utils/toast'
import { useSession } from 'next-auth/react'
import { useEffect, useState } from 'react'

/**
 * This component checks for any pending addresses saved during checkout
 * when the user was not signed in, and processes them when they sign in.
 */
export default function PendingAddressProcessor() {
  const { data: session, status } = useSession()
  const pendingAddresses = useAppSelector((state: RootState) => state.pendingData?.pendingAddresses || [])
  const dispatch = useAppDispatch()
  const [processed, setProcessed] = useState(false)

  useEffect(() => {
    // Only process addresses if user has signed in and we have pending addresses
    // and we haven't processed them before (to prevent repeated processing)
    if (
      status === 'authenticated' &&
      session?.user?.id &&
      pendingAddresses.length > 0 &&
      !processed
    ) {
      const processPendingAddresses = async () => {
        try {
          const result = await processPendingAddressesAction(pendingAddresses)

          if (result.success && result.data) {
            // Clear pending addresses after successful processing
            dispatch(clearPendingAddresses())
            setProcessed(true)

            // Show success message if addresses were processed
            if (result.data.processedCount > 0) {
              showToast({
                title: `${result.data.processedCount} saved ${
                  result.data.processedCount === 1 ? 'address' : 'addresses'
                } added to your account`,
                color: 'success',
                timeout: 3000
              })
            }
          }
        } catch (error) {
          console.error('Failed to process pending addresses', error)
        }
      }

      processPendingAddresses()
    }
  }, [session, status, pendingAddresses, dispatch, processed])

  // This is a utility component that doesn't render anything
  return null
}
